import Vue from 'vue';
import VueRouter, { RouteConfig, Location } from 'vue-router';

import { AuthModule } from '@/store/modules/Auth';
import { check } from '@/api/auth';
import { UserType } from '../../../common/dist/common';

Vue.use(VueRouter);

const checkLogin = async () => {
  const ret = await check();
  await AuthModule.setLogin({
    isLogin: (ret.result && ret.data?.success) || false,
    type: ret.data?.type || UserType.User,
    isAccepted: ret.data?.isAccepted || false,
    name: ret.data?.name,
    email: ret.data?.email,
    nonce: ret.data?.nonce || '',
  });
};

interface RouteMeta {
  requireTypes: UserType[];
  redirect: Location;
}

const appChildren: RouteConfig[] = [
  {
    path: '',
    redirect: 'top',
  },
  {
    path: 'top',
    component: () => import('@/views/Top.vue'),
  },
  {
    path: 'input/:date',
    component: () => import('@/views/InputView.vue'),
    props: true,
  },
  {
    path: 'input',
    component: () => import('@/views/InputView.vue'),
  },
  {
    path: 'list',
    component: () => import('@/components/ListComponent.vue'),
  },
  {
    path: 'schedule',
    component: () => import('@/views/schedule/ScheduleList.vue'),
  },
  {
    path: 'schedule/edit/new',
    component: () => import('@/views/schedule/ScheduleEdit.vue'),
    props: {
      isNew: true,
    },
  },
  {
    path: 'schedule/edit/:id',
    component: () => import('@/views/schedule/ScheduleEdit.vue'),
    props: true,
  },
  {
    path: 'schedule/edit/:id/events',
    component: () => import('@/views/schedule/ScheduleEventEdit.vue'),
    props: true,
  },
  {
    path: 'schedule/edit/:id/timetable',
    component: () => import('@/views/schedule/ScheduleTimetableEdit.vue'),
    props: true,
  },
  {
    path: 'report',
    component: () => import('@/components/ReportComponent.vue'),
  },
  {
    path: 'setting',
    component: () => import('@/views/SettingView.vue'),
  },
];

appChildren.forEach((v) => {
  const meta: RouteMeta = {
    requireTypes: [UserType.User, UserType.Admin, UserType.Manager],
    redirect: { name: 'login', params: { error: 'Session Expired' } },
  };
  v.meta = meta;
});

const adminRoute: RouteConfig = {
  path: 'admin',
  component: () => import('@/views/admin/AdminTop.vue'),
  children: [
    {
      path: 'users',
      component: () => import('@/views/admin/UserList.vue'),
    },
    {
      path: 'groups',
      component: () => import('@/views/admin/GroupList.vue'),
    },
    {
      path: 'control',
      component: () => import('@/views/admin/ControlPanel.vue'),
    },
    {
      path: 'limit',
      component: () => import('@/views/admin/LimitSetting.vue'),
    },
    {
      path: 'specialleave',
      component: () => import('@/views/admin/SpecialLeaveSetting.vue'),
    },
  ],
};

if (adminRoute.children) {
  adminRoute.children.forEach((v) => {
    const meta: RouteMeta = {
      requireTypes: [UserType.Admin],
      redirect: { name: 'login', params: { error: 'Session Expired' } },
    };
    v.meta = meta;
  });
}

const manageRoute: RouteConfig[] = [
  {
    path: 'manage/groups/:groupId?/:userId?/:type?/:date?',
    component: () => import('@/views/manage/ManageGroupTop.vue'),
  },
];
manageRoute.forEach((v) => {
  const meta: RouteMeta = {
    requireTypes: [UserType.Admin, UserType.Manager],
    redirect: { name: 'login', params: { error: 'Session Expired' } },
  };
  v.meta = meta;
});

const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    props: true,
  },
  {
    path: '/adminlogin',
    component: () => import('@/views/AdminLogin.vue'),
  },
  {
    path: '/app/',
    component: () => import('@/views/App.vue'),
    children: [
      ...appChildren,
      ...manageRoute,
      adminRoute,
      {
        path: '*',
        redirect: 'top',
      },
    ],
  },
  {
    path: '*',
    redirect: '/app/top',
  },
];

if (process.env.VUE_APP_TESTMODE === 'test') {
  routes.push({
    path: '/testlogin',
    component: () => import('@/TestLogin.vue'),
  });
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  await checkLogin();

  const { meta } = to;
  if (!(meta && meta.redirect && meta.requireTypes)) {
    return next();
  }

  if (!AuthModule.isLogin) {
    return next({ name: 'login', params: { error: 'Session Expired' } });
  }

  if (!meta.requireTypes.includes(AuthModule.type)) {
    return next(meta.redirect);
  }
  return next();
});

export default router;
